import { call, race, select, takeEvery, put } from 'redux-saga/effects';
import {
  assoc,
  append,
  path,
  pathOr,
  head,
  prop,
  pipe,
  mergeLeft,
  keys,
  reduce,
  reject,
  propOr,
  concat,
} from 'ramda';
import { notificationsAdd } from 'virtues-bootstrap';
import { createMatchSelector, push } from 'connected-react-router';
import { getFormValues, startSubmit, stopSubmit, reset } from 'redux-form';
import { hide as hideModal } from 'redux-modal';

import { CANCELLED } from 'constants/observationStatuses';
import { DELETE_REPORT, DELETE_CARDIO_VASCULAR_REPORT } from 'constants/modals';
import {
  fetchReportItem,
  reportsDownloadPdf,
  requestReportAttachment,
  downloadDocumentReferencePdf,
  reportsMarkAsRead,
  requestReportItemSuccess,
  reportsSubmitDetailedEpForm,
  reportsSetObservation,
  reportsSubmitDeviceInfoForm,
  deleteReport,
  deleteCardioVascularReport,
} from 'store/actionCreators/reports/singleItems';

import {
  requestDocumentReference,
  requestDeleteDocumentReference,
} from 'store/actionCreators/documentReference';

import {
  getReportItemData,
  getReportItemOpenedDateTime,
  getReportAdditionalDocsId,
  getReportAdditionalDocsData,
} from 'store/selectors/reports/singleItems';
import { createSagaRequestRace, createSagaRace } from 'utilities/request';
import { b64toBlob, downloadBlob, getUniqId } from 'utilities';
import { requestObservationUpdate } from 'store/actionCreators/deviceStatuses';
import { getUserRole, getUserId } from 'store/selectors/user';
import { getPatientPageUrlId } from 'store/selectors/router';
import { PATIENT, SUPER_RESEARCHER, SUPER_TECHNICIAN } from 'constants/userRoles';
import {
  FORM_NAMES,
  ATRIAL_DEPENDENT_FIELD_VALUES,
  VENTRICULAR_DEPENDENT_FIELD_VALUES,
} from 'constants/forms';
import i18n from 'services/i18n';
import {
  getObservationValue,
  getDetailedReportObservationValue,
  isComponentCodeDetailedReportFormField,
  isComponentCodeEditDeviceInfoFormField,
  getObservationShockValues,
} from 'utilities/fhir/observation';
import { requestPatientQuestionnaireResponse } from 'store/actionCreators/patientQuestionnaireResponse';
import { getPatientQuestionnaireResponseData } from 'store/selectors/patientQuestionnaireResponse';
import { requestReportItem } from 'services/api/request';

export function* onFetchReportItem({ payload: { id } }) {
  try {
    const report = yield select(getReportItemData(id));
    const role = yield select(getUserRole);

    if (!report) {
      const { data } = yield call(requestReportItem, { id });

      let additionalDocuments = [];
      if (role !== PATIENT) {
        const additionalDocumentsId = getReportAdditionalDocsId(data);

        if (additionalDocumentsId) {
          const isSuperRole = [SUPER_RESEARCHER, SUPER_TECHNICIAN].includes(role);
          const userId = yield select(getUserId);
          yield put(
            requestDocumentReference({
              id: additionalDocumentsId,
              elements: '-content,extension',
              ...(!isSuperRole && { author: userId }),
            }),
          );
          const { success } = yield race(createSagaRequestRace(requestDocumentReference));
          additionalDocuments = getReportAdditionalDocsData(success.payload.response);
        }
      }

      yield put({
        type: String(requestReportItemSuccess),
        payload: {
          data,
          additionalDocuments,
          id,
        },
        meta: { primaryPayload: { id } },
      });
    }

    const qResponseData = yield select(getPatientQuestionnaireResponseData);

    if (role === PATIENT && !qResponseData) {
      const patientId = yield select(getUserId);
      yield put(requestPatientQuestionnaireResponse({ id: patientId }));
    }
  } catch (error) {
    yield put(
      notificationsAdd({
        id: getUniqId(),
        variant: 'danger',
        title: i18n.t('common:requestError'),
        description: error.toString(),
      }),
    );
  }
}

export function* onRequestReportItemSuccess({ payload: { id } }) {
  const role = yield select(getUserRole);
  if (role !== PATIENT) {
    return;
  }

  const data = yield select(getReportItemData(id));
  const isOpened = yield select(getReportItemOpenedDateTime(id));

  if (!isOpened) {
    const updatedObservation = assoc(
      'component',
      append(
        {
          code: {
            coding: [{ code: 'opened_by_patient', display: 'Patient opened this report' }],
          },
          valueDateTime: new Date().toISOString(),
        },
        prop('component', data),
      ),
      data,
    );

    yield put(requestObservationUpdate({ data: updatedObservation, id }));
  }
}

export function* handleDownloadDocumentReferencePdf({ payload: { url, fileName = 'report' } }) {
  const newFileName = `${fileName}.pdf`;

  yield put(requestReportAttachment({ url }));

  const { success, failure } = yield race(createSagaRequestRace(requestReportAttachment));

  if (failure) {
    return;
  }

  const { data, contentType } = pipe(
    pathOr([], ['payload', 'response', 'content']),
    head,
    prop('attachment'),
  )(success);

  const blob = b64toBlob(data, contentType);
  const objectUrl = URL.createObjectURL(blob);

  yield call(downloadBlob, objectUrl, newFileName);
}

export function* onMarkAsRead() {
  const match = yield select(createMatchSelector('/report/:id'));
  const observationId = path(['params', 'id'], match);

  const data = yield select(getReportItemData(observationId));

  const updatedObservation = mergeLeft(
    {
      status: 'final',
      component: append(
        {
          code: {
            coding: [{ code: 'patient_review_datetime', display: 'Patient reviewed date & time' }],
          },
          valueDateTime: new Date().toISOString(),
        },
        prop('component', data),
      ),
    },
    data,
  );

  yield put(requestObservationUpdate({ data: updatedObservation, id: observationId }));
  yield race(createSagaRequestRace(requestObservationUpdate));
}

export function* onReportsSubmitDetailedEpForm({ payload: { reportId } }) {
  yield put(startSubmit(FORM_NAMES.detailedEPReport));
  const values = yield select(getFormValues(FORM_NAMES.detailedEPReport));
  const observation = yield select(getReportItemData(reportId));

  const components = reduce(
    (acc, key) => [...acc, ...getDetailedReportObservationValue(values[key], key)],
    [],
    keys(values),
  );

  const atrialRadioValue = prop('anyAtrialArrhythmiasDetected', values);
  const ventricularRadioValue = prop('anyVentricularArrhythmiasDetected', values);

  const editedObservation = assoc(
    'component',
    pipe(
      propOr([], 'component'),
      reject(({ code }) =>
        isComponentCodeDetailedReportFormField(path(['coding', '0', 'code'])(code)),
      ),
      concat(components),
      // !important should be after concat
      reject(
        ({ code }) =>
          atrialRadioValue === 'No' &&
          ATRIAL_DEPENDENT_FIELD_VALUES.includes(path(['coding', '0', 'code'])(code)),
      ),
      reject(
        ({ code }) =>
          ventricularRadioValue === 'No' &&
          VENTRICULAR_DEPENDENT_FIELD_VALUES.includes(path(['coding', '0', 'code'])(code)),
      ),
    )(observation),
    observation,
  );

  yield put(requestObservationUpdate({ data: editedObservation, id: reportId }));
  const { success } = yield createSagaRace(requestObservationUpdate);
  if (success) {
    yield put(
      notificationsAdd({
        id: getUniqId(),
        variant: 'success',
        title: i18n.t('common:requestSuccess'),
        description: i18n.t('common:reports.wasUpdated'),
      }),
    );
    yield put(reportsSetObservation({ data: editedObservation, id: reportId }));
    yield put(reset(FORM_NAMES.detailedEPReport));
  }

  yield put(stopSubmit(FORM_NAMES.detailedEPReport));
}

export function* onSubmitDeviceInfoForm({ payload: { reportId } }) {
  yield put(startSubmit(FORM_NAMES.editDeviceStatus));
  const values = yield select(getFormValues(FORM_NAMES.editDeviceStatus));
  const observation = yield select(getReportItemData(reportId));

  const components = concat(
    reduce((acc, k) => [...acc, ...getObservationValue(values[k], k)], [], keys(values)),
    getObservationShockValues(values.shocksDate),
  );

  const editedObservation = assoc(
    'component',
    pipe(
      propOr([], 'component'),
      reject(isComponentCodeEditDeviceInfoFormField),
      concat(components),
    )(observation),
    observation,
  );

  yield put(requestObservationUpdate({ data: editedObservation, id: reportId }));
  const { success } = yield createSagaRace(requestObservationUpdate);
  if (success) {
    yield put(
      notificationsAdd({
        id: getUniqId(),
        variant: 'success',
        title: i18n.t('common:requestSuccess'),
        description: i18n.t('common:reports.wasUpdated'),
      }),
    );
    yield put(reportsSetObservation({ data: editedObservation, id: reportId }));
    yield put(reset(FORM_NAMES.editDeviceStatus));
  }
  yield put(stopSubmit(FORM_NAMES.editDeviceStatus));
}

export function* handleDeleteReport({ payload }) {
  try {
    const { reportId } = payload;
    const reportData = yield select(getReportItemData(reportId));
    const updatedReportData = {
      ...reportData,
      status: CANCELLED,
    };

    yield put(requestObservationUpdate({ data: updatedReportData, id: reportId }));

    const { success } = yield createSagaRace(requestObservationUpdate);

    if (success) {
      yield put(
        notificationsAdd({
          id: getUniqId(),
          variant: 'success',
          title: i18n.t('common:requestSuccess'),
          description: i18n.t('common:reports.wasUpdated'),
        }),
      );
      const patientId = yield select(getPatientPageUrlId);
      yield put(hideModal(DELETE_REPORT));

      yield put(push(`/patient/${patientId}`));
    }
  } catch (error) {
    yield put(
      notificationsAdd({
        id: getUniqId(),
        variant: 'danger',
        title: i18n.t('common:requestError'),
        description: error.toString(),
      }),
    );
  }
}

export function* handleDeleteCardioVascularReport({ payload }) {
  const { reportId: id, onPageChange } = payload;

  yield put(requestDeleteDocumentReference(id));

  const { success } = yield createSagaRace(requestDeleteDocumentReference);

  if (success) {
    yield put(
      notificationsAdd({
        id: getUniqId(),
        variant: 'success',
        title: i18n.t('common:requestSuccess'),
        description: i18n.t('common:reports.cardioReportRemovedSuccess'),
      }),
    );

    yield put(hideModal(DELETE_CARDIO_VASCULAR_REPORT));
    yield onPageChange({ type: 'REFRESH_TABLE', payload: id });
  }
}

export default function*() {
  yield takeEvery(fetchReportItem, onFetchReportItem);
  yield takeEvery(deleteReport, handleDeleteReport);
  yield takeEvery(deleteCardioVascularReport, handleDeleteCardioVascularReport);
  yield takeEvery(reportsDownloadPdf, handleDownloadDocumentReferencePdf);
  yield takeEvery(downloadDocumentReferencePdf, handleDownloadDocumentReferencePdf);
  yield takeEvery(reportsMarkAsRead, onMarkAsRead);
  yield takeEvery(requestReportItemSuccess, onRequestReportItemSuccess);
  yield takeEvery(reportsSubmitDetailedEpForm, onReportsSubmitDetailedEpForm);
  yield takeEvery(reportsSubmitDeviceInfoForm, onSubmitDeviceInfoForm);
}
